import * as THREE from 'three';
import type { BodyFit, LiveryPainter, LiveryViews } from './types';

/**
 * livery/atlas.ts (M144) — one texture, four views.
 *
 * The side / top / front / rear paintings of a livery are packed into ONE
 * square atlas so a dressed car still costs a single paint texture. The paint
 * material's shader (patchLiveryMaterial) reads two per-vertex attributes the
 * projection wrote once per geometry — where the vertex sits inside the
 * measured body box and which way its surface faces — and picks the view per
 * PIXEL, cross-fading between "this is flank" and "this is hood" over the
 * roll of the body's own shoulder line.
 *
 * An optional DATA atlas rides in the same layout for showpiece liveries
 * (R = relief height, G = glow mask, B = roughness): scales that catch the
 * garage lights, fire that actually glows.
 */

/** Region rectangles in atlas PIXELS at the 1024 base size. */
const BASE = 1024;
const GUTTER = 16;
const SIDE_H = 352;
const END_W = 384;
const END_H = BASE - 2 * (SIDE_H + GUTTER); // 288
const REGIONS = {
  side: { x: 0, y: 0, w: BASE, h: SIDE_H },
  top: { x: 0, y: SIDE_H + GUTTER, w: BASE, h: SIDE_H },
  front: { x: 0, y: 2 * (SIDE_H + GUTTER), w: END_W, h: END_H },
  rear: { x: END_W + GUTTER, y: 2 * (SIDE_H + GUTTER), w: END_W, h: END_H },
} as const;
type RegionKey = keyof typeof REGIONS;

/** A region as a UV rectangle (x0, y0, w, h) with v running UP (flipY). */
function uvRect(key: RegionKey): THREE.Vector4 {
  const r = REGIONS[key];
  return new THREE.Vector4(r.x / BASE, 1 - (r.y + r.h) / BASE, r.w / BASE, r.h / BASE);
}

function makeViews(fit: BodyFit, res: number): { views: LiveryViews; canvases: Record<RegionKey, HTMLCanvasElement> } {
  const k = res / BASE;
  const mk = (key: RegionKey, physW: number, physH: number): [HTMLCanvasElement, CanvasRenderingContext2D] => {
    const r = REGIONS[key];
    const c = document.createElement('canvas');
    c.width = Math.round(r.w * k);
    c.height = Math.round(r.h * k);
    const g = c.getContext('2d')!;
    g.scale(c.width / physW, c.height / physH);
    return [c, g];
  };
  const [sc, side] = mk('side', fit.L, fit.H);
  const [tc, top] = mk('top', fit.L, fit.W);
  const [fc, front] = mk('front', fit.W, fit.H);
  const [rc, rear] = mk('rear', fit.W, fit.H);
  return {
    views: { side, top, front, rear, fit },
    canvases: { side: sc, top: tc, front: fc, rear: rc },
  };
}

/** Lay the four view canvases into one atlas, bleeding each region's edges
 *  into the gutter so mip levels never pull a neighbour's colour across. */
function compose(canvases: Record<RegionKey, HTMLCanvasElement>, res: number, fill: string): HTMLCanvasElement {
  const k = res / BASE;
  const atlas = document.createElement('canvas');
  atlas.width = atlas.height = res;
  const g = atlas.getContext('2d')!;
  g.fillStyle = fill;
  g.fillRect(0, 0, res, res);
  const pad = Math.round((GUTTER / 2) * k);
  (Object.keys(REGIONS) as RegionKey[]).forEach((key) => {
    const r = REGIONS[key];
    const c = canvases[key];
    const x = Math.round(r.x * k);
    const y = Math.round(r.y * k);
    const w = c.width;
    const h = c.height;
    // Edge bleed first (one-pixel rows / columns stretched outward)…
    g.drawImage(c, 0, 0, w, 1, x, y - pad, w, pad);
    g.drawImage(c, 0, h - 1, w, 1, x, y + h, w, pad);
    g.drawImage(c, 0, 0, 1, h, x - pad, y, pad, h);
    g.drawImage(c, w - 1, 0, 1, h, x + w, y, pad, h);
    // …then the view itself.
    g.drawImage(c, x, y);
  });
  return atlas;
}

/** Paint a livery and hand back just its SIDE view (the flat painting the
 *  placeholder body and the bulldozer form wear). `w` = pixels wide. */
export function buildLiverySide(paint: LiveryPainter, fit: BodyFit, w = 512): HTMLCanvasElement {
  const { views, canvases } = makeViews(fit, w);
  paint(views);
  return canvases.side;
}

export interface LiveryAtlas {
  map: THREE.CanvasTexture;
  /** R relief height, G glow mask, B roughness. Absent for plain liveries. */
  data?: THREE.CanvasTexture;
}

export interface AtlasOptions {
  /** Atlas edge in pixels (1024 standard, 2048 for showpiece wraps). */
  res?: number;
  /** Also paint the relief / glow / gloss atlas. */
  data?: boolean;
}

/** Paint a livery for one measured body and pack it into its atlas texture. */
export function buildLiveryAtlas(paint: LiveryPainter, fit: BodyFit, opts: AtlasOptions = {}): LiveryAtlas {
  const res = opts.res ?? BASE;
  const colour = makeViews(fit, res);
  // The data atlas is half the colour size: relief and glow are soft signals.
  const dataRes = Math.max(512, res / 2);
  const data = opts.data ? makeViews(fit, dataRes) : null;
  if (data) {
    // Neutral ground: no relief (mid grey would bias the bump; 0 = flat low),
    // no glow, mid roughness.
    for (const key of ['side', 'top', 'front', 'rear'] as const) {
      const g = data.views[key];
      g.save();
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.fillStyle = 'rgb(128,0,96)';
      g.fillRect(0, 0, g.canvas.width, g.canvas.height);
      g.restore();
    }
  }
  paint(colour.views, data?.views);

  const map = new THREE.CanvasTexture(compose(colour.canvases, res, '#808080'));
  map.colorSpace = THREE.SRGBColorSpace;
  map.wrapS = map.wrapT = THREE.ClampToEdgeWrapping;
  map.anisotropy = 8;
  map.userData.lv3 = true;
  const out: LiveryAtlas = { map };
  if (data) {
    const d = new THREE.CanvasTexture(compose(data.canvases, dataRes, 'rgb(128,0,96)'));
    d.colorSpace = THREE.NoColorSpace;
    d.wrapS = d.wrapT = THREE.ClampToEdgeWrapping;
    d.anisotropy = 4;
    out.data = d;
    map.userData.lvData = d;
  }
  return out;
}

// ---------------------------------------------------------------------------
// The paint shader patch.
// ---------------------------------------------------------------------------

const V4 = (v: THREE.Vector4): string =>
  `vec4(${v.x.toFixed(6)}, ${v.y.toFixed(6)}, ${v.z.toFixed(6)}, ${v.w.toFixed(6)})`;

const FRAG_PARS = /* glsl */ `
varying vec3 vLvPos;
varying vec3 vLvNrm;
uniform vec2 lvCut;
uniform float lvMirror;
const vec4 LV_SIDE = ${V4(uvRect('side'))};
const vec4 LV_TOP = ${V4(uvRect('top'))};
const vec4 LV_FRONT = ${V4(uvRect('front'))};
const vec4 LV_REAR = ${V4(uvRect('rear'))};
// Half-width (in normal units) of the cross-fade either side of a cut.
const float LV_SOFT = 0.055;
// One atlas coordinate + its screen derivatives per view (side, top, end),
// and how much of the top and the end view this pixel takes (the flank takes
// the rest).
vec2 lvUvS; vec2 lvDxS; vec2 lvDyS;
vec2 lvUvT; vec2 lvDxT; vec2 lvDyT;
vec2 lvUvE; vec2 lvDxE; vec2 lvDyE;
float lvWT;
float lvWE;
// Resolve where this pixel sits in every view. The derivatives are taken per
// view (each is continuous), so the mip level never spikes along a seam.
// The views CROSS-FADE over a short band of the surface normal: a hard cut
// follows the mesh's triangles, and on a fender (boolean-cut, sliver
// triangles) that drew a ragged, torn edge (measured in the garage).
void lvResolve() {
  vec3 n = normalize( vLvNrm );
  vec3 p = clamp( vLvPos, 0.004, 0.996 );
  vec3 px = dFdx( vLvPos );
  vec3 py = dFdy( vLvPos );
  vec2 q = vec2( p.z, p.y );
  vec2 qx = vec2( px.z, px.y );
  vec2 qy = vec2( py.z, py.y );
  // A halved shirt is not the same on both flanks: the far flank reads the
  // side painting back to front (nose ↔ tail) when the livery asks for it.
  if ( lvMirror > 0.5 && p.x > 0.5 ) {
    q.x = 1.0 - q.x;
    qx.x = -qx.x;
    qy.x = -qy.x;
  }
  lvUvS = LV_SIDE.xy + q * LV_SIDE.zw;
  lvDxS = qx * LV_SIDE.zw;
  lvDyS = qy * LV_SIDE.zw;
  lvUvT = LV_TOP.xy + vec2( p.z, p.x ) * LV_TOP.zw;
  lvDxT = vec2( px.z, px.x ) * LV_TOP.zw;
  lvDyT = vec2( py.z, py.x ) * LV_TOP.zw;
  vec4 r = p.z < 0.5 ? LV_FRONT : LV_REAR;
  lvUvE = r.xy + vec2( p.x, p.y ) * r.zw;
  lvDxE = vec2( px.x, px.y ) * r.zw;
  lvDyE = vec2( py.x, py.y ) * r.zw;
  lvWT = smoothstep( lvCut.x - LV_SOFT, lvCut.x + LV_SOFT, n.y );
  lvWE = ( 1.0 - lvWT ) * smoothstep( lvCut.y - LV_SOFT, lvCut.y + LV_SOFT, abs( n.z ) );
}
// Read an atlas through the resolved views. (textureGrad throughout: the
// lookups sit inside branches, where implicit derivatives are undefined.)
vec4 lvSample( sampler2D tex, vec2 oS, vec2 oT, vec2 oE ) {
  vec4 c = vec4( 0.0 );
  float wS = 1.0 - lvWT - lvWE;
  if ( wS > 0.003 ) c += wS * textureGrad( tex, lvUvS + oS, lvDxS, lvDyS );
  if ( lvWT > 0.003 ) c += lvWT * textureGrad( tex, lvUvT + oT, lvDxT, lvDyT );
  if ( lvWE > 0.003 ) c += lvWE * textureGrad( tex, lvUvE + oE, lvDxE, lvDyE );
  return c;
}
`;

const FRAG_MAP = /* glsl */ `
#ifdef USE_MAP
  lvResolve();
  vec4 sampledDiffuseColor = lvSample( map, vec2( 0.0 ), vec2( 0.0 ), vec2( 0.0 ) );
  diffuseColor *= sampledDiffuseColor;
#endif
`;

/** Relief: three's own screen-space bump, fed from the resolved views. */
const FRAG_BUMP_PARS = /* glsl */ `
#ifdef USE_BUMPMAP
  uniform sampler2D bumpMap;
  uniform float bumpScale;
  vec2 dHdxy_fwd() {
    float Hll = bumpScale * lvSample( bumpMap, vec2( 0.0 ), vec2( 0.0 ), vec2( 0.0 ) ).x;
    float dBx = bumpScale * lvSample( bumpMap, lvDxS, lvDxT, lvDxE ).x - Hll;
    float dBy = bumpScale * lvSample( bumpMap, lvDyS, lvDyT, lvDyE ).x - Hll;
    return vec2( dBx, dBy );
  }
  vec3 perturbNormalArb( vec3 surf_pos, vec3 surf_norm, vec2 dHdxy, float faceDirection ) {
    vec3 vSigmaX = normalize( dFdx( surf_pos.xyz ) );
    vec3 vSigmaY = normalize( dFdy( surf_pos.xyz ) );
    vec3 vN = surf_norm;
    vec3 R1 = cross( vSigmaY, vN );
    vec3 R2 = cross( vN, vSigmaX );
    float fDet = dot( vSigmaX, R1 ) * faceDirection;
    vec3 vGrad = sign( fDet ) * ( dHdxy.x * R1 + dHdxy.y * R2 );
    return normalize( abs( fDet ) * surf_norm - vGrad );
  }
#endif
`;

/** Gloss: the data atlas' blue channel IS the roughness. */
const FRAG_ROUGH = /* glsl */ `
float roughnessFactor = roughness;
#ifdef USE_BUMPMAP
  vec4 lvData = lvSample( bumpMap, vec2( 0.0 ), vec2( 0.0 ), vec2( 0.0 ) );
  roughnessFactor = clamp( lvData.b, 0.04, 1.0 );
#endif
`;

/** Glow: the painted colour, lit by the data atlas' green channel. */
const FRAG_EMISSIVE = /* glsl */ `
#ifdef USE_BUMPMAP
  totalEmissiveRadiance += diffuseColor.rgb * lvData.g * lvGlow;
#endif
`;

export interface LiveryShaderOptions {
  /** The data atlas (relief / glow / gloss), when the livery has one. */
  data?: THREE.Texture;
  /** Relief strength (three's bumpScale). */
  bump?: number;
  /** Glow strength (multiplies the painted colour in the glow mask). */
  glow?: number;
  /** n.y above this = the TOP view; |n.z| above the second = an END view. */
  cut?: [number, number];
  /** The far flank reads the side painting nose ↔ tail (halved shirts). */
  mirror?: boolean;
}

/**
 * Teach a paint material to read a three-view atlas. Every patched material
 * shares one program (two with the data atlas), whatever livery it wears.
 */
export function patchLiveryMaterial(mat: THREE.MeshPhysicalMaterial, opts: LiveryShaderOptions = {}): void {
  const cut = new THREE.Vector2(opts.cut?.[0] ?? 0.6, opts.cut?.[1] ?? 0.72);
  const glow = { value: opts.glow ?? 1 };
  if (opts.data) {
    mat.bumpMap = opts.data;
    mat.bumpScale = opts.bump ?? 1;
  }
  const hasData = !!opts.data;
  const mirror = { value: opts.mirror ? 1 : 0 };
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.lvCut = { value: cut };
    shader.uniforms.lvGlow = glow;
    shader.uniforms.lvMirror = mirror;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute vec3 lvPos;\nattribute vec3 lvNrm;\nvarying vec3 vLvPos;\nvarying vec3 vLvNrm;',
      )
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvLvPos = lvPos;\nvLvNrm = lvNrm;');
    let f = shader.fragmentShader
      .replace('#include <map_pars_fragment>', `#include <map_pars_fragment>\nuniform float lvGlow;\n${FRAG_PARS}`)
      .replace('#include <map_fragment>', FRAG_MAP);
    if (hasData) {
      f = f
        .replace('#include <bumpmap_pars_fragment>', FRAG_BUMP_PARS)
        .replace('#include <roughnessmap_fragment>', FRAG_ROUGH)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${FRAG_EMISSIVE}`);
    }
    shader.fragmentShader = f;
  };
  mat.customProgramCacheKey = () => (hasData ? 'lv3-data' : 'lv3');
}

// ---------------------------------------------------------------------------
// M154 polish r5 — a PLAN-VIEW decal over a flag wrap (the Formula).
// ---------------------------------------------------------------------------

/** n.y above this (cross-faded over LV_SOFT) takes the decal. */
export const DECAL_CUT = 0.6;

/**
 * Teach a flag wrap's paint material (the side painting through the plain
 * UVs, untouched) to lay a plan-view decal over the surfaces that face UP:
 * where the decal is opaque and the smoothed view normal points up, its
 * colour replaces the wrap's. `minY` (0..1 of the body's height) keeps it
 * off anything up-facing below the tops it was drawn for. Reads the same
 * `lvPos` / `lvNrm` attributes as the three-view livery.
 */
export function patchWrapDecal(mat: THREE.MeshPhysicalMaterial, decal: THREE.Texture, minY: number): void {
  const decalU = { value: decal };
  const minYU = { value: minY };
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.lvDecal = decalU;
    shader.uniforms.lvDecalMinY = minYU;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nattribute vec3 lvPos;\nattribute vec3 lvNrm;\nvarying vec3 vLvPos;\nvarying vec3 vLvNrm;',
      )
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvLvPos = lvPos;\nvLvNrm = lvNrm;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <map_pars_fragment>',
        '#include <map_pars_fragment>\nvarying vec3 vLvPos;\nvarying vec3 vLvNrm;\nuniform sampler2D lvDecal;\nuniform float lvDecalMinY;',
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
{
  vec3 lvN = normalize( vLvNrm );
  vec4 lvD = texture2D( lvDecal, vec2( vLvPos.z, vLvPos.x ) );
  float lvUp = smoothstep( ${(DECAL_CUT - 0.055).toFixed(3)}, ${(DECAL_CUT + 0.055).toFixed(3)}, lvN.y ) * step( lvDecalMinY, vLvPos.y );
  diffuseColor.rgb = mix( diffuseColor.rgb, lvD.rgb, lvD.a * lvUp );
}`,
      );
  };
  mat.customProgramCacheKey = () => 'lv-decal';
}
